/* Sad Boy Saga — theme interaction layer.
 *
 * Handles every interactive element on the theme end-to-end:
 *  - Drawer + modal open/close (cart, nav, filter, size guide, picker)
 *  - Size-grid selection with live variant + price update (main PDP)
 *  - Sticky ATC picker hydration + inline cart-add
 *  - Main PDP form intercept (AJAX to /cart/add.js, no reload)
 *  - Cart line qty +/- and Remove with inline drawer re-render
 *  - Collection-page filter chips + CLEAR ALL + sort select
 *  - Product-card quick-add opens a mini-picker (fetches product JSON)
 *  - Session-persistent drawer state across page reloads
 *
 * All cart mutations go through /cart/{add,change}.js (public Shopify
 * storefront endpoints). Prices come back in cents; we format client-side.
 */
(function () {
  'use strict';

  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  /* ---------- utilities ---------- */
  function escapeHtml(s) {
    if (s == null) return '';
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
  function fmtMoney(cents) {
    const n = Number(cents || 0) / 100;
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function withWidth(url, w) {
    if (!url) return '';
    const sep = url.indexOf('?') === -1 ? '?' : '&';
    return url + sep + 'width=' + w;
  }
  function toast(msg) {
    let t = $('.toast');
    if (!t) { t = document.createElement('div'); t.className = 'toast'; document.body.appendChild(t); }
    t.textContent = msg;
    t.classList.add('show');
    clearTimeout(toast._t);
    toast._t = setTimeout(() => t.classList.remove('show'), 2200);
  }

  /* ---------- overlay + drawer helpers ---------- */
  function openOverlay() { const ov = $('.cart-overlay'); if (ov) ov.classList.add('open'); document.body.classList.add('no-scroll'); }
  function closeOverlay() { const ov = $('.cart-overlay'); if (ov) ov.classList.remove('open'); document.body.classList.remove('no-scroll'); }
  function setAriaHidden(el, hidden) { if (el) el.setAttribute('aria-hidden', hidden ? 'true' : 'false'); }
  function openCart() { const el = $('.cart-drawer'); if (!el) return; el.classList.add('open'); setAriaHidden(el, false); openOverlay(); }
  function closeCart() { const el = $('.cart-drawer'); if (el) { el.classList.remove('open'); setAriaHidden(el, true); } if (!$('.nav-drawer.open') && !$('.filter-drawer.open')) closeOverlay(); }
  function openNav() { const el = $('.nav-drawer'); if (!el) return; el.classList.add('open'); setAriaHidden(el, false); openOverlay(); }
  function closeNav() { const el = $('.nav-drawer'); if (el) { el.classList.remove('open'); setAriaHidden(el, true); } if (!$('.cart-drawer.open') && !$('.filter-drawer.open')) closeOverlay(); }
  function openFilter() { const el = $('.filter-drawer'); if (!el) return; el.classList.add('open'); setAriaHidden(el, false); openOverlay(); }
  function closeFilter() { const el = $('.filter-drawer'); if (el) { el.classList.remove('open'); setAriaHidden(el, true); } if (!$('.cart-drawer.open') && !$('.nav-drawer.open')) closeOverlay(); }
  function openSizeGuide(e) {
    if (e && e.preventDefault) e.preventDefault();
    const m = $('.size-modal'); if (!m) return;
    // Toggle the appropriate body based on picker/PDP context. Categories:
    //   * footwear-sneaker  — Skywalker V2 sneaker sizing (AF1)
    //   * footwear-sandal   — Hidden Gems / any sandal (Birkenstock-style)
    //   * apparel           — tees, longsleeves, hoodies, shorts, sets
    // JS-M-03 (v9): only trust picker state when the picker is CURRENTLY
    // OPEN — otherwise stale `data-productHandle` from a prior product
    // (buyer opened picker for a sandal, closed it, later opens the size
    // guide from a Skywalker PDP surface) would toggle the wrong body.
    const picker = $('[data-ctf-picker]');
    const pickerOpen = picker && !picker.hasAttribute('hidden');
    const type = pickerOpen ? (picker.dataset.productType || '').toLowerCase() : '';
    const handle = pickerOpen ? (picker.dataset.productHandle || '').toLowerCase() : '';
    const footwearEl = m.querySelector('[data-size-guide-body-footwear]');
    const sneakerEl = m.querySelector('[data-size-guide-body-footwear-sneaker]');
    const sandalEl = m.querySelector('[data-size-guide-body-footwear-sandal]');
    const apparelEl = m.querySelector('[data-size-guide-body-apparel]');
    // Sandal signals: type/handle contain 'sandal', or handle is a known
    // sandal SKU ('hidden-gems' → Birk-alike). Sneaker is the default for
    // any other footwear (skywalker, trainer, etc.).
    const isSandal = (type && /sandal/.test(type)) || (handle && (/sandal/.test(handle) || /hidden/.test(handle)));
    const isFootwear = isSandal || (type && /shoe|sneaker|footwear|trainer/.test(type)) || (handle && /skywalker|trainer/.test(handle));
    const isApparel = type && /tee|shirt|longsleeve|long sleeve|short|hoodie|set|pant/.test(type);
    // Only toggle when we have SOMETHING to disambiguate on. If context
    // is empty (no picker), show every section (safe default).
    const hasContext = !!(type || handle);
    if (hasContext) {
      // Footwear vs apparel outer toggle
      if (footwearEl) footwearEl.style.display = (isFootwear || (!isFootwear && !isApparel)) ? '' : 'none';
      if (apparelEl) apparelEl.style.display = (isApparel || (!isFootwear && !isApparel)) ? '' : 'none';
      // Inner sneaker vs sandal toggle (Skywalker → sneaker; Hidden Gems → sandal)
      if (sneakerEl) sneakerEl.style.display = (isFootwear && !isSandal) ? '' : (isFootwear ? 'none' : '');
      if (sandalEl) sandalEl.style.display = (isFootwear && isSandal) ? '' : (isFootwear ? 'none' : '');
    } else {
      if (footwearEl) footwearEl.style.display = '';
      if (apparelEl) apparelEl.style.display = '';
      if (sneakerEl) sneakerEl.style.display = '';
      if (sandalEl) sandalEl.style.display = '';
    }
    m.classList.add('open');
    m.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
  }
  function closeSizeGuide() {
    const m = $('.size-modal'); if (m) { m.classList.remove('open'); m.setAttribute('aria-hidden', 'true'); }
    if (!$('.cart-drawer.open') && !$('.nav-drawer.open') && !$('.filter-drawer.open')) document.body.classList.remove('no-scroll');
  }
  function closePicker() {
    const p = $('[data-ctf-picker]'); if (!p) return;
    p.setAttribute('hidden', '');
    // JS-H-04 (v9): reset picker state defensively on close so any future
    // openPicker() from a code path that doesn't call a hydrate function
    // never inherits stale variant/size state from a prior product.
    const atc = p.querySelector('[data-ctf-picker-atc]');
    if (atc) {
      atc.classList.add('needs-size');
      atc.textContent = 'SELECT A SIZE';
      delete atc.dataset.variantId;
    }
    p.querySelectorAll('.size-btn.selected').forEach((b) => {
      b.classList.remove('selected');
      b.setAttribute('aria-pressed', 'false');
    });
  }
  function openPicker() { const p = $('[data-ctf-picker]'); if (p) p.removeAttribute('hidden'); }

  /* ---------- cart API ---------- */
  async function cartAdd(variantId, quantity) {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: Number(quantity || 1) }] })
    });
    if (!r.ok) {
      let msg = 'Add to cart failed';
      try { const b = await r.json(); msg = b.description || b.message || msg; } catch (_) {}
      throw new Error(msg);
    }
    return r.json();
  }
  async function cartChange(lineKey, quantity) {
    const r = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: Number(quantity) })
    });
    if (!r.ok) throw new Error('Cart update failed');
    return r.json();
  }
  async function cartFetch() {
    const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    // JS-M-07 (v9): guard non-2xx so a 500/503 doesn't return the error
    // body as `cart`, which would make item_count undefined → drawer
    // silently shows empty-state despite server holding items.
    if (!r.ok) throw new Error('Cart fetch failed');
    return r.json();
  }

  /* ---------- inline cart drawer rendering ---------- */
  // FREE_SHIP_THRESHOLD (15000) deleted 2026-07-30 along with its bar — US shipping
  // is free at $0 minimum on the live delivery profile, so there is no threshold to
  // count toward. Any future threshold must be read from the server, never hardcoded:
  // this constant had already drifted twice (a since-expired $85 rule, then $150 copy)
  // while the live rule moved elsewhere.

  // Low-stock lookup, emitted by sbs-cart-drawer.liquid. /cart.js carries NO inventory
  // data, so without this map every "Only N left" line would vanish the moment a
  // mutation re-rendered the drawer. Keyed by variant id; only tracked+deny variants
  // with 1..5 remaining are present. Read defensively — an absent/!ok blob is normal
  // on pages that render the drawer before the map, and must degrade to "no badge",
  // never to a wrong number.
  let LOW_STOCK = {};
  try {
    const lsEl = document.querySelector('[data-sbs-low-stock]');
    if (lsEl) LOW_STOCK = JSON.parse(lsEl.textContent) || {};
  } catch (e) { LOW_STOCK = {}; }

  function lowStockLine(variantId) {
    const n = LOW_STOCK[String(variantId)];
    if (!n || n < 1 || n > 5) return '';
    return `<div class="cart-line__stock" data-low-stock="${n}">Only ${n} left in this size</div>`;
  }

  function renderCartLine(item) {
    const imgSrc = item.image ? withWidth(item.image, 136) : '';
    const variantLine = item.variant_title && item.variant_title !== 'Default Title'
      ? `<div class="cart-line__variant">${escapeHtml(item.variant_title)}</div>` : '';
    return `
      <div class="cart-line" data-cart-line="${escapeHtml(item.key)}">
        <a class="cart-line__img" href="${escapeHtml(item.url)}">
          ${imgSrc ? `<img src="${escapeHtml(imgSrc)}" alt="${escapeHtml(item.product_title || item.title)}" width="68" height="68" loading="lazy">` : ''}
        </a>
        <div class="cart-line__info">
          <a class="cart-line__title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title || item.title)}</a>
          ${variantLine}
          ${lowStockLine(item.variant_id)}
          <div class="qty-control">
            <button type="button" class="qty-btn" data-qty-decrement aria-label="Decrease">−</button>
            <span class="qty-value">${item.quantity}</span>
            <button type="button" class="qty-btn" data-qty-increment aria-label="Increase">+</button>
          </div>
        </div>
        <div>
          <div class="cart-line__price">${escapeHtml(fmtMoney(item.final_line_price))}</div>
          <button type="button" class="cart-line__remove" data-cart-remove>Remove</button>
        </div>
      </div>`;
  }

  function renderCartDrawer(cart) {
    // Notify sbs-freeshoe.js (and any other listeners) of the current cart so
    // the free-shoe offer nudge/modal stays in sync after every mutation.
    try { document.dispatchEvent(new CustomEvent('sbs:cart:updated', { detail: cart })); } catch (_) {}
    const count = cart.item_count || 0;
    const empty = $('.cart-empty');
    const items = $('[data-cart-items]');
    const upsell = $('[data-cart-upsell]');
    const footer = $('.cart-drawer__footer');
    // Header count
    $$('.cart-count').forEach((el) => { el.textContent = count; el.style.display = count > 0 ? '' : 'none'; });
    $$('.cart-count-large').forEach((el) => { el.textContent = '(' + count + ')'; });
    // Floating pill
    $$('[data-floating-cart-count]').forEach((el) => { el.textContent = count; });
    $$('[data-floating-cart-total]').forEach((el) => { el.textContent = fmtMoney(cart.total_price); });
    const pill = $('[data-floating-cart]');
    if (pill) { if (count > 0) pill.removeAttribute('hidden'); else pill.setAttribute('hidden', ''); }
    // Free-ship bar REMOVED 2026-07-30. It counted toward a hardcoded $150 and
    // announced "FREE SHIPPING UNLOCKED" there, but the live delivery config gives
    // US shipping free at $0 minimum (default profile: "Standard Shipping
    // (3-5 Business Days)" $0.00, no conditions). The bar invented friction that
    // did not exist and under-sold the real offer. The slot now holds an OFFER
    // progress bar ([data-offer-bar]) owned by sbs-freeshoe.js, which is the module
    // that holds offer state — this renderer must not also write it, or the two
    // would race on every cart mutation.
    // Empty vs populated — toggle display on ALL affected containers so
    // an initially-empty drawer can populate on the first add without
    // needing a page reload.
    if (count === 0) {
      if (empty) empty.style.display = '';
      if (items) { items.style.display = 'none'; items.innerHTML = ''; }
      if (upsell) upsell.setAttribute('hidden', '');
      if (footer) footer.style.display = 'none';
      return;
    }
    if (empty) empty.style.display = 'none';
    // JS-M-01 (v9): capture scrollTop before we blow away innerHTML so a
    // mid-scroll add doesn't yank the buyer back to the top of the drawer.
    const scroller = $('.cart-drawer__scroll');
    const scrollTop = scroller ? scroller.scrollTop : 0;
    if (items) { items.style.display = ''; items.innerHTML = cart.items.map(renderCartLine).join(''); }
    if (scroller) scroller.scrollTop = scrollTop;
    // Only reveal the upsell block if anchor-driven items exist. Empty
    // upsell containers with just the "Complete the fit" header look broken.
    if (upsell) {
      const upsellItems = upsell.querySelector('[data-cart-upsell-items]');
      const hasChildren = upsellItems && upsellItems.children.length > 0;
      if (hasChildren) upsell.removeAttribute('hidden');
      else upsell.setAttribute('hidden', '');
    }
    if (footer) footer.style.display = '';
    // Subtotal
    $$('.cart-subtotal-total').forEach((el) => { el.textContent = fmtMoney(cart.total_price); });
  }

  async function refreshCart(openDrawerAfter) {
    // On the cart page, inline drawer refresh isn't enough — the line
    // list on the page won't update. Reload instead.
    if (document.querySelector('.sbs-cart-page')) {
      window.location.reload();
      return;
    }
    // 2026-07-09 speed pass: open the drawer BEFORE awaiting the fetch.
    // Perceived latency drops from ~400ms (network + render) to ~30ms
    // (drawer slide-in). The fetch fills in updated totals under the
    // buyer while the animation plays.
    if (openDrawerAfter) openCart();
    try {
      const cart = await cartFetch();
      renderCartDrawer(cart);
    } catch (err) {
      // Fallback to reload if inline refresh breaks
      if (openDrawerAfter) sessionStorage.setItem('sbs_open_cart', '1');
      window.location.reload();
    }
  }

  /* 2026-07-09 speed pass: ATC helper that opens the drawer BEFORE the
     network call fires. Drawer slides in showing the current cart items;
     the new line replaces the stale render ~200ms later. Buyer perceives
     ~30ms instead of ~500ms (cartAdd + cartFetch + render sequential).

     Second pass (2026-07-09 v9): optimistic line render. Before cartAdd
     resolves, we prepend a projection of the new line using the product
     data we already know (title, image, price from the picker/PDP).
     Buyer sees the item in the drawer INSTANTLY on drawer-open instead of
     waiting ~400ms for the network round-trip. On response, renderCartDrawer
     replaces the projection with the server-truth line + line key. */
  function renderOptimisticLine(proj) {
    if (!proj || !proj.title) return;
    const items = $('[data-cart-items]');
    const empty = $('.cart-empty');
    const footer = $('.cart-drawer__footer');
    if (!items) return;
    if (empty) empty.style.display = 'none';
    items.style.display = '';
    if (footer) footer.style.display = '';
    const imgSrc = proj.image ? withWidth(proj.image, 136) : '';
    const variantLine = proj.variantTitle
      ? `<div class="cart-line__variant">${escapeHtml(proj.variantTitle)}</div>` : '';
    const priceLine = proj.price ? escapeHtml(proj.price) : '';
    const optimisticHtml = `
      <div class="cart-line" data-cart-line-optimistic style="opacity:0.85">
        <div class="cart-line__img">
          ${imgSrc ? `<img src="${escapeHtml(imgSrc)}" alt="" width="68" height="68">` : ''}
        </div>
        <div class="cart-line__info">
          <span class="cart-line__title">${escapeHtml(proj.title)}</span>
          ${variantLine}
          <div class="qty-control">
            <button type="button" class="qty-btn" aria-label="Decrease" disabled>−</button>
            <span class="qty-value">${proj.quantity || 1}</span>
            <button type="button" class="qty-btn" aria-label="Increase" disabled>+</button>
          </div>
        </div>
        <div>
          <div class="cart-line__price">${priceLine}</div>
        </div>
      </div>`;
    items.insertAdjacentHTML('afterbegin', optimisticHtml);
    // JS-H-06 (v9): optimistic count bump must touch every header target
    // (.cart-count small badge, .cart-count-large parenthetical, floating
    // pill count). Only touching .cart-count causes header numbers to
    // disagree for ~200-500ms until renderCartDrawer reconciles.
    const q = proj.quantity || 1;
    $$('.cart-count').forEach((el) => {
      const n = Number(el.textContent || 0) + q;
      el.textContent = n;
      el.style.display = '';
    });
    $$('.cart-count-large').forEach((el) => {
      const current = Number((el.textContent || '(0)').replace(/[()]/g, '')) || 0;
      el.textContent = '(' + (current + q) + ')';
    });
    $$('[data-floating-cart-count]').forEach((el) => {
      const n = Number(el.textContent || 0) + q;
      el.textContent = n;
    });
  }
  function rollbackOptimistic(qty) {
    // v9 speed pass: on ANY optimistic-add failure, purge every optimistic
    // line (querySelectorAll — double-tap can leave >1) AND decrement each
    // count target we optimistically bumped in renderOptimisticLine.
    document.querySelectorAll('[data-cart-line-optimistic]').forEach((n) => n.remove());
    const q = Number(qty || 1);
    $$('.cart-count').forEach((el) => {
      const n = Math.max(0, Number(el.textContent || 0) - q);
      el.textContent = n;
      el.style.display = n > 0 ? '' : 'none';
    });
    $$('.cart-count-large').forEach((el) => { el.textContent = '(' + Math.max(0, Number((el.textContent || '(0)').replace(/[()]/g, '')) - q) + ')'; });
    $$('[data-floating-cart-count]').forEach((el) => { el.textContent = Math.max(0, Number(el.textContent || 0) - q); });
  }
  async function addAndOpen(vid, quantity, optimistic) {
    const wasClosed = !$('.cart-drawer.open');
    openCart();
    const q = quantity || 1;
    if (optimistic) renderOptimisticLine(Object.assign({ quantity: q }, optimistic));
    // JS-H-05 (v9): split the two awaits so a cartAdd success + cartFetch
    // failure doesn't tell the buyer their add failed when it actually
    // succeeded. Add-fail → toast + rollback + close drawer if we opened
    // it. Fetch-fail after add-success → the item IS in the cart, so
    // reload (page will re-render cart drawer from server truth).
    try {
      await cartAdd(vid, q);
    } catch (err) {
      toast(err.message || 'Add to cart failed');
      rollbackOptimistic(q);
      if (wasClosed) closeCart();
      return;
    }
    try {
      const cart = await cartFetch();
      renderCartDrawer(cart);
    } catch (err) {
      sessionStorage.setItem('sbs_open_cart', '1');
      window.location.reload();
    }
  }

  function consumeCartShouldOpen() {
    if (sessionStorage.getItem('sbs_open_cart') === '1') {
      sessionStorage.removeItem('sbs_open_cart');
      openCart();
    }
  }

  /* ---------- PDP product data + variant handling ---------- */
  function getProductData() {
    const el = $('[data-product-json]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (_) { return null; }
  }

  function updatePDPForVariant(variant) {
    if (!variant) return;
    // Main PDP price display
    $$('[data-price-display]').forEach((el) => { el.textContent = fmtMoney(variant.price); });
    // Sticky ATC price + installments
    $$('[data-sticky-atc-price], [data-sticky-price]').forEach((el) => { el.textContent = fmtMoney(variant.price); });
    // Installment line
    const inst = $('.pdp-installments');
    if (inst) {
      const q = Math.round(variant.price / 4);
      inst.textContent = inst.textContent.replace(/installments of \$[\d,.]+/i, 'installments of ' + fmtMoney(q));
    }
    // Main PDP form hidden id
    const form = $('#sbs-product-form');
    const input = form && form.querySelector('input[name="id"]');
    if (input) input.value = variant.id;
    // Meta on the info div
    const info = $('[data-product-info]');
    if (info) info.dataset.selectedVariant = variant.id;
    // Sold-out state on main ATC. Both branches must fire — the previous
    // implementation only handled the sold-out case, so once an ATC read
    // "SOLD OUT" it never went back to "ADD TO CART" after picking an
    // available size. See Phase 5 finding.
    const atc = $('.pdp-cta [data-atc]');
    if (atc) {
      // JS-M-10 (v9): reuse outer `info` const instead of shadowing.
      const hasSize = info && info.dataset.selectedSize;
      if (!variant.available) {
        atc.classList.add('needs-size'); // reuse muted-clickable
        atc.classList.add('sold-out');
        atc.textContent = 'SOLD OUT';
        atc.disabled = false; // still clickable to trigger a toast on submit
      } else {
        atc.classList.remove('sold-out');
        // Only clear needs-size if a size is actually chosen — otherwise
        // buyer needs the sad-path scroll+nudge.
        if (hasSize) {
          atc.classList.remove('needs-size');
          atc.textContent = 'ADD TO CART';
        } else {
          atc.classList.add('needs-size');
          atc.textContent = 'SELECT A SIZE';
        }
      }
    }
  }

  /* ---------- accordions ---------- */
  document.addEventListener('click', (e) => {
    const trg = e.target.closest('.accordion-trigger');
    if (!trg) return;
    e.preventDefault();
    const acc = trg.closest('.accordion');
    if (!acc) return;
    const willOpen = !acc.classList.contains('open');
    acc.classList.toggle('open', willOpen);
    trg.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
  });
  // Initialize aria-expanded on accordions marked open in Liquid.
  document.querySelectorAll('.accordion.open .accordion-trigger').forEach((t) => t.setAttribute('aria-expanded', 'true'));

  /* ---------- PDP size grid selection ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.pdp-info .size-grid .size-btn, .pdp .size-grid .size-btn');
    if (!btn) return;
    // A sold-out size used to return in silence. The strike-through says "unavailable"
    // to someone reading carefully; a tap that produces nothing at all reads as broken
    // to everyone else. Same class of failure as the offer picker's silent refusals.
    if (btn.classList.contains('oos')) {
      e.preventDefault();
      toast(`Size ${btn.textContent.trim()} is sold out`);
      return;
    }
    const grid = btn.closest('.size-grid');
    $$('.size-btn', grid).forEach((b) => { b.classList.remove('selected'); b.setAttribute('aria-pressed', 'false'); });
    btn.classList.add('selected');
    btn.setAttribute('aria-pressed', 'true');
    // Look up variant, update price, hidden input, meta
    const vid = btn.getAttribute('data-variant-id');
    const data = getProductData();
    if (data && Array.isArray(data.variants) && vid) {
      const variant = data.variants.find((v) => String(v.id) === String(vid));
      if (variant) {
        updatePDPForVariant(variant);
        // Also keep the info dataset in sync so the sticky-ATC picker's
        // hydratePickerFromPDP reads the correct price for the chosen variant.
        const info = $('[data-product-info]');
        if (info) {
          info.dataset.price = (Number(variant.price) / 100).toFixed(2);
        }
      }
    }
    // Un-mute the main PDP ATC + set label
    const info = $('[data-product-info]');
    if (info) {
      info.dataset.selectedSize = btn.textContent.trim();
      $$('.needs-size').forEach((atc) => {
        atc.classList.remove('needs-size');
        if (atc.matches('.pdp-cta [data-atc]')) atc.textContent = 'ADD TO CART';
      });
    }
  });

  /* ---------- picker (used by sticky ATC + collection quick-add) ---------- */
  function hydratePickerFromPDP() {
    const picker = $('[data-ctf-picker]');
    const info = $('[data-product-info]');
    if (!picker || !info) return false;
    picker.dataset.pickerMode = 'pdp';
    // Carry the PDP product handle + type onto the picker so openSizeGuide
    // can pick the right size-guide body (sneaker vs sandal vs apparel)
    // when the buyer opens it from a sticky-ATC → picker fallback.
    picker.dataset.productHandle = info.dataset.id || '';
    const productData = getProductData();
    picker.dataset.productType = (productData && productData.type) || '';
    resetPickerAtc();
    const img = picker.querySelector('[data-ctf-picker-image]');
    const title = picker.querySelector('[data-ctf-picker-title]');
    const price = picker.querySelector('[data-ctf-picker-price]');
    if (img) img.src = info.dataset.image || '';
    if (title) title.textContent = info.dataset.title || '';
    if (price) price.textContent = fmtMoney(Number(info.dataset.price || 0) * 100);
    // Clone PDP sizes so data-variant-id travels
    const gridSrc = $('.pdp-info .size-grid') || $('.pdp .size-grid');
    const gridDst = picker.querySelector('[data-ctf-picker-sizes]');
    if (gridSrc && gridDst) {
      gridDst.innerHTML = '';
      gridSrc.querySelectorAll('.size-btn').forEach((b) => {
        const clone = b.cloneNode(true);
        clone.classList.remove('selected');
        gridDst.appendChild(clone);
      });
    }
    return true;
  }

  function resetPickerAtc() {
    const atc = $('[data-ctf-picker-atc]');
    if (!atc) return;
    atc.classList.add('needs-size');
    atc.textContent = 'SELECT A SIZE';
    delete atc.dataset.variantId;
  }

  async function hydratePickerFromHandle(handle) {
    if (!handle) return false;
    const picker = $('[data-ctf-picker]');
    if (!picker) return false;
    try {
      const r = await fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } });
      if (!r.ok) throw new Error('Product fetch failed');
      const p = await r.json();
      picker.dataset.pickerMode = 'collection';
      resetPickerAtc();
      // Cache the fetched product so the size-guide fallback can read
      // product.type / title without a second fetch.
      picker.dataset.productHandle = handle;
      picker.dataset.productType = p.type || '';
      const img = picker.querySelector('[data-ctf-picker-image]');
      const title = picker.querySelector('[data-ctf-picker-title]');
      const price = picker.querySelector('[data-ctf-picker-price]');
      if (img) img.src = p.featured_image ? withWidth(p.featured_image, 200) : '';
      if (title) title.textContent = p.title;
      if (price) price.textContent = fmtMoney(p.price);
      // Figure out the size option index (like the Liquid does). /products/<h>.js
      // returns options as EITHER an array of strings (legacy) OR array of
      // {name, values} objects. Test the name string in both shapes.
      const sizeIdx = (p.options || []).findIndex((o) => {
        const name = typeof o === 'string' ? o : (o && o.name) || '';
        return /size/i.test(name);
      });
      const optIdx = sizeIdx >= 0 ? sizeIdx : 0;
      const gridDst = picker.querySelector('[data-ctf-picker-sizes]');
      if (gridDst) {
        const values = (p.options && p.options[optIdx] && p.options[optIdx].values) || [];
        // Fallback for older Shopify shape (options: [strings])
        let optionsData;
        if (Array.isArray(p.options) && p.options.length && typeof p.options[0] === 'string') {
          // Legacy shape: no per-option values — read from variants
          optionsData = { name: p.options[optIdx], values: Array.from(new Set(p.variants.map((v) => v['option' + (optIdx + 1)]).filter(Boolean))) };
        } else {
          optionsData = { name: (p.options[optIdx] && p.options[optIdx].name) || 'Size', values: (p.options[optIdx] && p.options[optIdx].values) || [] };
        }
        gridDst.innerHTML = optionsData.values.map((val) => {
          const v = p.variants.find((vv) => vv['option' + (optIdx + 1)] === val);
          const oos = !v || !v.available ? ' oos' : '';
          const vid = v ? v.id : '';
          return `<button type="button" class="size-btn${oos}" data-size="${escapeHtml(val)}" data-variant-id="${vid}">${escapeHtml(val)}</button>`;
        }).join('');
      }
      return true;
    } catch (err) {
      // JS-H-08 (v9): don't toast on failure — the caller navigates to
      // the PDP as a recovery path, so the toast is invisible for exactly
      // 0ms as the page replaces. The navigation IS the recovery UX.
      return false;
    }
  }

  /* ---------- picker size click → SELECT ONLY (do not auto-add) ----------
     Prior behavior auto-added on size click, which surprised buyers who
     expected a confirmation ADD TO CART press before committing. Buyer
     feedback 2026-07-09: keep the size-select, un-mute the ADD TO CART
     button, but wait for the explicit press. */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-ctf-picker-sizes] .size-btn');
    if (!btn) return;
    e.preventDefault();
    // Same silent-sold-out fix as the PDP grid above. This picker is the quick-add
    // path off a collection card, where the buyer has no other signal at all.
    if (btn.classList.contains('oos')) {
      toast(`Size ${btn.textContent.trim()} is sold out`);
      return;
    }
    const vid = btn.getAttribute('data-variant-id');
    if (!vid) { toast('Size unavailable'); return; }
    $$('[data-ctf-picker-sizes] .size-btn').forEach((b) => { b.classList.remove('selected'); b.setAttribute('aria-pressed', 'false'); });
    btn.classList.add('selected');
    btn.setAttribute('aria-pressed', 'true');
    // Un-mute ADD TO CART on the picker + set label to full-color state.
    const atc = $('[data-ctf-picker-atc]');
    if (atc) {
      atc.classList.remove('needs-size');
      atc.dataset.variantId = vid;
      atc.textContent = 'ADD TO CART';
    }
  });

  /* ---------- picker ADD TO CART click ---------- */
  document.addEventListener('click', async (e) => {
    const atc = e.target.closest('[data-ctf-picker-atc]');
    if (!atc) return;
    e.preventDefault();
    // Sad path: nothing selected yet — the button is muted-clickable per
    // Phase 2 rule, so nudge the picker size grid instead of failing silent.
    if (atc.classList.contains('needs-size') || !atc.dataset.variantId) {
      const grid = $('[data-ctf-picker-sizes]');
      if (grid) {
        grid.classList.remove('size-grid--nudge');
        void grid.offsetWidth;
        grid.classList.add('size-grid--nudge');
      }
      return;
    }
    const vid = atc.dataset.variantId;
    // Snapshot picker projection BEFORE we close it — image/title/price/
    // size disappear on close but are the source-of-truth for the drawer's
    // optimistic render.
    const picker = $('[data-ctf-picker]');
    const selectedSizeBtn = picker && picker.querySelector('[data-ctf-picker-sizes] .size-btn.selected');
    const optimistic = picker ? {
      title: picker.querySelector('[data-ctf-picker-title]')?.textContent || '',
      image: picker.querySelector('[data-ctf-picker-image]')?.getAttribute('src') || '',
      price: picker.querySelector('[data-ctf-picker-price]')?.textContent || '',
      variantTitle: selectedSizeBtn ? selectedSizeBtn.textContent.trim() : ''
    } : null;
    closePicker();
    // Reset picker state for next open.
    atc.classList.add('needs-size');
    atc.textContent = 'SELECT A SIZE';
    delete atc.dataset.variantId;
    await addAndOpen(vid, 1, optimistic);
  });

  /* ---------- ATC clicks (main PDP + sticky + product-card quickadd) ---------- */
  document.addEventListener('click', async (e) => {
    // Product-card quick-add on a collection/rail card
    const quick = e.target.closest('.product-card__quickadd');
    if (quick) {
      e.preventDefault();
      if (quick.classList.contains('sold-out')) { toast('This piece is sold out'); return; }
      // Prefer explicit data-product-href (Phase 5 restructure), fall back
      // to the parent card's link href for backward compat with any older
      // rail markup that still wraps the whole card in <a>.
      const card = quick.closest('.product-card');
      const linkEl = card && (card.querySelector('.product-card__link') || card.matches('a[href]') && card);
      const href = quick.getAttribute('data-product-href') || (linkEl && linkEl.getAttribute('href'));
      if (!href) return;
      const m = href.match(/\/products\/([^/?#]+)/);
      const handle = m ? m[1] : null;
      if (!handle) { window.location.href = href; return; }
      const ok = await hydratePickerFromHandle(handle);
      if (ok) openPicker();
      else window.location.href = href;
      return;
    }

    const atc = e.target.closest('[data-atc]');
    if (!atc) return;

    const isSticky = !!atc.closest('.sticky-atc');
    const info = $('[data-product-info]');
    const selectedSize = info && info.dataset.selectedSize;

    if (isSticky) {
      e.preventDefault();
      if (!selectedSize) {
        if (hydratePickerFromPDP()) openPicker();
        else toast('Pick a size');
        return;
      }
      const form = $('#sbs-product-form');
      const input = form && form.querySelector('input[name="id"]');
      const vid = input && input.value;
      if (!vid) { toast('Pick a size'); return; }
      // Availability check before hitting /cart/add.js — the sticky path
      // previously bypassed the sold-out toast the main ATC has, so it
      // fired a POST that got a 422 and surfaced a raw Shopify error.
      const data = getProductData();
      if (data && data.variants) {
        const variant = data.variants.find((v) => String(v.id) === String(vid));
        if (variant && !variant.available) {
          toast('This size is sold out');
          return;
        }
      }
      await addAndOpen(vid, 1, pdpOptimisticProjection(selectedSize));
      return;
    }

    // Main PDP ATC without size → scroll to grid + nudge
    if (atc.classList.contains('needs-size')) {
      e.preventDefault();
      const grid = $('.pdp-info .size-grid') || $('.pdp .size-grid');
      if (grid) {
        grid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        grid.classList.remove('size-grid--nudge');
        void grid.offsetWidth;
        grid.classList.add('size-grid--nudge');
      }
      return;
    }
    // Sold-out main ATC → toast
    if (atc.textContent && /sold out/i.test(atc.textContent)) {
      e.preventDefault();
      toast('This size is sold out');
      return;
    }
    // Otherwise let native form submit run (caught below).
  });

  /* ---------- Intercept the main PDP form submit → AJAX ---------- */
  function pdpOptimisticProjection(sizeLabel) {
    const info = $('[data-product-info]');
    if (!info) return null;
    const priceDisplay = $('[data-price-display]');
    return {
      title: info.dataset.title || '',
      image: info.dataset.image || '',
      price: (priceDisplay && priceDisplay.textContent) || '',
      variantTitle: sizeLabel || info.dataset.selectedSize || ''
    };
  }
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('#sbs-product-form');
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector('input[name="id"]');
    const vid = input && input.value;
    if (!vid) { toast('Pick a size'); return; }
    const info = $('[data-product-info]');
    const size = info && info.dataset.selectedSize;
    await addAndOpen(vid, 1, pdpOptimisticProjection(size));
  });

  /* ---------- cart drawer: qty +/- + remove (optimistic UI) ----------
     2026-07-09 speed pass: buyer's tap updates the DOM IMMEDIATELY (qty
     text, or line fade-out for Remove). The /cart/change.js call fires in
     the background. On response, refreshCart reconciles totals + line
     prices. Perceived latency drops from ~400ms (sequential change+fetch)
     to ~30ms (instant DOM update).

     Rapid re-taps on the same line collapse into a single request via a
     150ms debounce. Prior CART_MUTATION_INFLIGHT dropped subsequent taps
     entirely, so a fast double-tap on "+" only bumped qty by 1. Now every
     tap counts — the pending target folds forward and the last one wins. */
  const CART_PENDING_QTY = new Map();
  const CART_DEBOUNCE = new Map();

  function optimisticQtyUpdate(line, target) {
    const qtyEl = line.querySelector('.qty-value');
    if (target <= 0) {
      // Kick off the line-remove animation. Line is fully hidden by the
      // time refreshCart re-renders, so no flash.
      line.style.transition = 'opacity 140ms ease, transform 140ms ease';
      line.style.opacity = '0';
      line.style.transform = 'translateX(30px)';
      line.style.pointerEvents = 'none';
    } else if (qtyEl) {
      qtyEl.textContent = target;
    }
  }

  document.addEventListener('click', (e) => {
    const line = e.target.closest('[data-cart-line]');
    if (!line) return;
    const dec = e.target.closest('[data-qty-decrement]');
    const inc = e.target.closest('[data-qty-increment]');
    const rem = e.target.closest('[data-cart-remove]');
    if (!dec && !inc && !rem) return;
    e.preventDefault();
    const lineKey = line.getAttribute('data-cart-line');
    if (!lineKey) return;

    // JS-H-03 (v9): once a line is queued for removal (target=0), reject
    // any subsequent +/- taps against the same line so a Remove doesn't
    // fold forward into a qty-1 change during the 150ms debounce window.
    if (line.dataset.pendingRemove === '1') return;

    const qtyEl = line.querySelector('.qty-value');
    const pending = CART_PENDING_QTY.get(lineKey);
    const current = Number(pending != null ? pending : ((qtyEl && qtyEl.textContent) || 1));
    const target = rem ? 0 : (inc ? current + 1 : Math.max(0, current - 1));

    CART_PENDING_QTY.set(lineKey, target);
    if (target === 0) line.dataset.pendingRemove = '1';
    optimisticQtyUpdate(line, target);

    // Debounce the network call. Rapid taps only send the last target.
    const prior = CART_DEBOUNCE.get(lineKey);
    if (prior) clearTimeout(prior);
    CART_DEBOUNCE.set(lineKey, setTimeout(async () => {
      const finalTarget = CART_PENDING_QTY.get(lineKey);
      CART_PENDING_QTY.delete(lineKey);
      CART_DEBOUNCE.delete(lineKey);
      try {
        // /cart/change.js RETURNS THE WHOLE CART. This used to throw that response away
        // and call refreshCart(), which fetched /cart.js to learn what the server had
        // just finished telling us — so every +/- tap cost two round trips instead of one.
        // On the cart PAGE the reload still has to happen, because the page's own line
        // list is server-rendered and this renderer only owns the drawer.
        const cart = await cartChange(lineKey, finalTarget);
        if (document.querySelector('.sbs-cart-page')) { window.location.reload(); return; }
        // CHECK, don't assume. `renderCartDrawer` reads item_count and items off this
        // object; handed something else it would paint an empty drawer over a cart that
        // has things in it — a worse bug than the round trip this saves. The shape is
        // documented and stable, but a documented shape is not a verified one, and the
        // fallback costs a fetch only on a path that would have fetched anyway.
        if (cart && typeof cart.item_count === 'number' && Array.isArray(cart.items)) {
          renderCartDrawer(cart);
        } else {
          await refreshCart(false);
        }
      } catch (err) {
        // JS-M-04 (v9): 404 from cartChange usually means the line was
        // already removed server-side — reconcile silently rather than
        // toasting a scary error.
        const isRemove = finalTarget === 0;
        const looksLikeGone = /404|not.?found|line.?item/i.test(err.message || '');
        if (!(isRemove && looksLikeGone)) {
          toast(err.message || (isRemove ? 'Remove failed' : 'Update failed'));
        }
        await refreshCart(false);
      }
    }, 150));
  });

  /* ---------- overlay + close-drawers ---------- */
  document.addEventListener('click', (e) => {
    if (e.target.matches('.cart-overlay, [data-close-drawers]')) {
      closeCart(); closeNav(); closeFilter();
    }
  });

  /* ---------- data-* delegated drawer/modal openers ----------
     Header/hero/close buttons previously used inline `onclick="openNav()"`
     etc., which race the deferred script load. A pre-boot tap silently
     no-op'd until sbs-app.js parsed and attached its window.* handlers.
     Delegate the same intents off `data-*` markers so the intent is bound
     at document level from the first parsed pass. */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-open-nav]')) { e.preventDefault(); openNav(); return; }
    if (e.target.closest('[data-open-cart]')) { e.preventDefault(); openCart(); return; }
    if (e.target.closest('[data-close-cart]')) { e.preventDefault(); closeCart(); return; }
    if (e.target.closest('[data-close-nav]')) { e.preventDefault(); closeNav(); return; }
    if (e.target.closest('[data-open-filter]')) { e.preventDefault(); openFilter(); return; }
    if (e.target.closest('[data-close-filter]')) { e.preventDefault(); closeFilter(); return; }
    if (e.target.closest('[data-open-size-guide]')) { openSizeGuide(e); return; }
    if (e.target.closest('[data-close-size-guide]')) { e.preventDefault(); closeSizeGuide(); return; }
  });

  /* ---------- checkout stale-cart guard ----------
     If the buyer's session cart expired server-side (TTL, incognito, stale
     tab) while the JS-driven drawer still shows items, tapping CHECKOUT
     would 302 to /cart. Pre-check /cart.js; only navigate if items exist. */
  document.addEventListener('click', async (e) => {
    const checkoutLink = e.target.closest('[data-checkout-link]');
    if (!checkoutLink) return;
    e.preventDefault();
    try {
      const cart = await cartFetch();
      if (!cart.item_count) {
        toast('Your cart is empty');
        await refreshCart(false);
        return;
      }
    } catch (_) { /* if the check fails, let native /checkout resolve */ }
    window.location.href = '/checkout';
  });

  /* ---------- focus trap for open drawer / modal ----------
     WCAG 2.4.3 focus order — keyboard users tabbing inside a dialog must
     not fall through to the (visually hidden) page beneath. Trap Tab
     within the currently-open surface, and restore focus to the trigger
     when it closes. */
  let LAST_FOCUS_TRIGGER = null;
  function activeSurface() {
    const modal = document.querySelector('.size-modal.open, [data-ctf-picker]:not([hidden])');
    if (modal) return modal;
    const drawer = document.querySelector('.cart-drawer.open, .nav-drawer.open, .filter-drawer.open');
    return drawer;
  }
  function focusablesIn(root) {
    const q = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(root.querySelectorAll(q)).filter((el) => el.offsetParent !== null || el === document.activeElement);
  }
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const root = activeSurface();
    if (!root) return;
    const items = focusablesIn(root);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });
  // Save the trigger so we can restore focus on close.
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open-cart], [data-open-nav], [data-open-filter], [data-open-size-guide]');
    if (opener) LAST_FOCUS_TRIGGER = opener;
  }, true);
  function restoreFocus() { if (LAST_FOCUS_TRIGGER && LAST_FOCUS_TRIGGER.focus) { try { LAST_FOCUS_TRIGGER.focus(); } catch (_) {} } }
  // Watch the open-state class on every surface and restore focus once
  // it comes off. This works regardless of who called the close (inline
  // onclick, delegated handler, ESC, overlay click).
  // JS-M-02 (v9): prior implementation used `r.oldValue` semantics which
  // return `null` when an attribute is newly ADDED (picker close sets the
  // `hidden` attr where none existed). That made `wasOpen` false → focus
  // restore never fired on picker close. Track per-element open state on
  // the element itself instead of parsing oldValue.
  const _closeObserver = new MutationObserver((records) => {
    records.forEach((r) => {
      if (r.attributeName !== 'class' && r.attributeName !== 'hidden') return;
      const el = r.target;
      const isPicker = el.hasAttribute('data-ctf-picker');
      const isOpen = isPicker ? !el.hasAttribute('hidden') : el.classList.contains('open');
      if (el._sbsWasOpen && !isOpen) requestAnimationFrame(restoreFocus);
      el._sbsWasOpen = isOpen;
    });
  });
  document.querySelectorAll('.cart-drawer, .nav-drawer, .filter-drawer, .size-modal, [data-ctf-picker]').forEach((el) => {
    const isPicker = el.hasAttribute('data-ctf-picker');
    el._sbsWasOpen = isPicker ? !el.hasAttribute('hidden') : el.classList.contains('open');
    _closeObserver.observe(el, { attributes: true, attributeFilter: ['class', 'hidden'] });
  });

  /* ---------- PDP gallery thumb → swap main image ----------
     Buyer walk 2026-07-09: thumb→main swap was slow because every click
     fetched a fresh `?width=1400` transform from Shopify CDN. First hit on
     a transform is cold (500ms-2s); subsequent are instant. Prewarm the CDN
     for every thumb's target `?width=1200` transform on gallery init so
     later clicks land instantly. Uses `?width=1200` (not 1400) — the visible
     main image is capped by the viewport so 1200 is enough at 2x DPR and
     transforms faster. */
  function prewarmGalleryTransforms() {
    document.querySelectorAll('[data-pdp-gallery]').forEach((gallery) => {
      gallery.querySelectorAll('[data-pdp-thumb] img').forEach((thumbImg) => {
        const raw = thumbImg.getAttribute('src') || '';
        if (!raw) return;
        let bumped = raw;
        if (/width=\d+/.test(bumped)) bumped = bumped.replace(/width=\d+/, 'width=1200');
        else bumped = bumped + (bumped.indexOf('?') === -1 ? '?' : '&') + 'width=1200';
        // Store on the thumb so click is a pure src assignment (no rebuild).
        thumbImg.dataset.mainSrc = bumped;
        // Fire-and-forget preload — the browser will cache the transform.
        const pre = new Image();
        pre.decoding = 'async';
        pre.src = bumped;
      });
    });
  }

  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-pdp-thumb]');
    if (!thumb) return;
    e.preventDefault();
    const gallery = thumb.closest('[data-pdp-gallery]');
    if (!gallery) return;
    const main = gallery.querySelector('.pdp-gallery-main img');
    const thumbImg = thumb.querySelector('img');
    if (main && thumbImg) {
      const cached = thumbImg.dataset.mainSrc;
      let src = cached || thumbImg.getAttribute('src') || '';
      if (!cached && src) {
        if (/width=\d+/.test(src)) src = src.replace(/width=\d+/, 'width=1200');
        else src = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=1200';
      }
      // Clear srcset FIRST so the browser doesn't ignore the new src while
      // still resolving the old srcset for the current viewport.
      if (main.hasAttribute('srcset')) main.removeAttribute('srcset');
      if (main.hasAttribute('sizes')) main.removeAttribute('sizes');
      main.src = src;
    }
    gallery.querySelectorAll('[data-pdp-thumb]').forEach((t) => { t.classList.remove('active'); t.setAttribute('aria-pressed', 'false'); });
    thumb.classList.add('active');
    thumb.setAttribute('aria-pressed', 'true');
  });

  /* ---------- picker close + size guide bridge ---------- */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-ctf-picker-close]')) closePicker();
    if (e.target.closest('[data-open-sg-from-picker]')) {
      // Keep picker context (product handle/type) alive so openSizeGuide
      // reads the right category — don't close the picker before opening.
      openSizeGuide();
    }
  });
  /* Size-modal backdrop click closes the modal. */
  document.addEventListener('click', (e) => {
    const modal = e.target.closest('[data-close-on-backdrop]');
    if (modal && e.target === modal) closeSizeGuide();
  });

  /* ---------- Collection filters ---------- */
  const FILTER_STATE = { silhouettes: new Set(), colors: new Set(), prices: new Set() };

  function chipSelectorFor(kind, value) {
    if (kind === 'silhouettes') return `[data-silhouette-chip="${value}"]`;
    if (kind === 'colors') return `[data-color-chip="${value}"]`;
    return `[data-price-chip="${value}"]`;
  }
  function toggleChipState(kind, value, on) {
    const set = FILTER_STATE[kind];
    if (on === undefined) on = !set.has(value);
    if (on) set.add(value); else set.delete(value);
    $$(chipSelectorFor(kind, value)).forEach((el) => {
      if (on) el.classList.add('active', 'selected');
      else el.classList.remove('active', 'selected');
      el.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    applyFilters();
    updateFilterCount();
    persistFilterStateToURL();
  }
  function getPriceThresholds() {
    const grid = $('[data-collection-grid]');
    const low = Number((grid && grid.getAttribute('data-price-low')) || 150);
    const high = Number((grid && grid.getAttribute('data-price-high')) || 200);
    return { low, high };
  }
  function cardMatches(card) {
    const sil = FILTER_STATE.silhouettes;
    const col = FILTER_STATE.colors;
    const prc = FILTER_STATE.prices;
    if (sil.size > 0) {
      const cs = (card.getAttribute('data-silhouette') || '').toLowerCase();
      if (!sil.has(cs)) return false;
    }
    if (col.size > 0) {
      // Colors may come from Liquid as either comma- OR space-separated —
      // SITE_GUIDE.md says commas but grid.liquid used to escape/split by
      // space. Normalize both.
      const cc = (card.getAttribute('data-colors') || '').toLowerCase().split(/[\s,]+/).filter(Boolean);
      let any = false; col.forEach((v) => { if (cc.indexOf(v) !== -1) any = true; });
      if (!any) return false;
    }
    if (prc.size > 0) {
      const p = Number(card.getAttribute('data-price') || 0);
      const th = getPriceThresholds();
      let any = false;
      prc.forEach((chip) => {
        if (chip === 'under-low' && p < th.low) any = true;
        else if (chip === 'low-high' && p >= th.low && p <= th.high) any = true;
        else if (chip === 'over-high' && p > th.high) any = true;
      });
      if (!any) return false;
    }
    return true;
  }
  function applyFilters() {
    $$('[data-collection-grid] .product-card').forEach((card) => {
      card.style.display = cardMatches(card) ? '' : 'none';
    });
  }
  function updateFilterCount() {
    const n = FILTER_STATE.silhouettes.size + FILTER_STATE.colors.size + FILTER_STATE.prices.size;
    $$('[data-filter-count]').forEach((el) => {
      el.textContent = n;
      el.style.display = n > 0 ? '' : 'none';
    });
  }
  /* Persist filter state to URL so LOAD MORE + refresh keep the buyer's
     active chips. Encoded as ?f_sil=a,b&f_col=c&f_prc=under-low. */
  function persistFilterStateToURL() {
    if (!$('[data-collection-grid]')) return;
    const url = new URL(window.location.href);
    const put = (k, set) => {
      if (set.size === 0) url.searchParams.delete(k);
      else url.searchParams.set(k, Array.from(set).join(','));
    };
    put('f_sil', FILTER_STATE.silhouettes);
    put('f_col', FILTER_STATE.colors);
    put('f_prc', FILTER_STATE.prices);
    window.history.replaceState(null, '', url.toString());
    // Also propagate the same params onto LOAD MORE href so page 2 arrives
    // with the same filters selected.
    const loadMore = $('[data-load-more]');
    if (loadMore) {
      try {
        const lm = new URL(loadMore.getAttribute('href'), window.location.origin);
        ['f_sil', 'f_col', 'f_prc'].forEach((k) => {
          const v = url.searchParams.get(k);
          if (v) lm.searchParams.set(k, v); else lm.searchParams.delete(k);
        });
        loadMore.setAttribute('href', lm.pathname + lm.search + lm.hash);
      } catch (_) { /* if URL parsing fails, leave LOAD MORE alone */ }
    }
  }
  function hydrateFilterStateFromURL() {
    if (!$('[data-collection-grid]')) return;
    const url = new URL(window.location.href);
    const pull = (k, kind) => {
      const raw = url.searchParams.get(k);
      if (!raw) return;
      raw.split(',').filter(Boolean).forEach((v) => toggleChipState(kind, v, true));
    };
    pull('f_sil', 'silhouettes');
    pull('f_col', 'colors');
    pull('f_prc', 'prices');
  }
  document.addEventListener('click', (e) => {
    const sil = e.target.closest('[data-silhouette-chip]');
    if (sil) { e.preventDefault(); toggleChipState('silhouettes', sil.getAttribute('data-silhouette-chip')); return; }
    const col = e.target.closest('[data-color-chip]');
    if (col) { e.preventDefault(); toggleChipState('colors', col.getAttribute('data-color-chip')); return; }
    const prc = e.target.closest('[data-price-chip]');
    if (prc) { e.preventDefault(); toggleChipState('prices', prc.getAttribute('data-price-chip')); return; }
    if (e.target.closest('[data-clear-filters]')) {
      e.preventDefault();
      FILTER_STATE.silhouettes.clear(); FILTER_STATE.colors.clear(); FILTER_STATE.prices.clear();
      $$('[data-silhouette-chip], [data-color-chip], [data-price-chip]').forEach((el) => {
        el.classList.remove('active', 'selected');
        el.setAttribute('aria-pressed', 'false');
      });
      applyFilters(); updateFilterCount(); persistFilterStateToURL();
    }
  });

  /* ---------- Sort select ---------- */
  document.addEventListener('change', (e) => {
    const sel = e.target.closest('[data-sort-by]');
    if (!sel) return;
    const v = sel.value;
    const url = new URL(window.location.href);
    if (v && v !== 'manual') url.searchParams.set('sort_by', v);
    else url.searchParams.delete('sort_by');
    window.location.href = url.toString();
  });

  /* ---------- Esc key (JS-H-07 v9: close only the top-most overlay) ---------- */
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    // Stack order top-to-bottom: size guide → picker → filter → nav → cart.
    // Prior implementation nuked all five on a single ESC — a buyer who
    // opened Size Guide FROM the picker lost picker state on one keystroke.
    if ($('.size-modal.open')) { closeSizeGuide(); return; }
    if ($('[data-ctf-picker]:not([hidden])')) { closePicker(); return; }
    if ($('.filter-drawer.open')) { closeFilter(); return; }
    if ($('.nav-drawer.open')) { closeNav(); return; }
    if ($('.cart-drawer.open')) { closeCart(); return; }
  });

  /* ---------- sticky ATC visibility (PDP) ---------- */
  function initStickyAtc() {
    const sticky = $('.sticky-atc');
    const primary = $('.pdp-cta [data-atc]');
    if (!sticky || !primary) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) sticky.classList.remove('visible');
        else sticky.classList.add('visible');
      });
    }, { rootMargin: '-100px 0px 0px 0px' });
    io.observe(primary);
  }

  /* ---------- boot ---------- */
  function boot() {
    initStickyAtc();
    consumeCartShouldOpen();
    hydrateFilterStateFromURL();
    prewarmGalleryTransforms();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* ---------- expose inline-onclick handlers ---------- */
  window.openCart = openCart; window.closeCart = closeCart;
  window.openNav = openNav; window.closeNav = closeNav;
  window.openFilter = openFilter; window.closeFilter = closeFilter;
  window.openSizeGuide = openSizeGuide; window.closeSizeGuide = closeSizeGuide;
  window.sbsToast = toast;
  // Exposed for sbs-freeshoe.js: re-render the drawer from a cart object after a free-shoe add.
  window.sbsRenderCart = renderCartDrawer;
  /* Also for sbs-freeshoe.js (2026-08-02). The offer's add is a /cart/add.js plus a
     reconcile read — ~400-700ms on a phone — and until now the drawer sat completely
     still for all of it, which is indistinguishable from a tap that did nothing. This is
     the same optimistic projection the PDP's add-to-cart already uses; the offer module
     had no way to reach it because it lives inside this IIFE.
     Rollback is exposed with it, deliberately: an optimistic line with no way to take it
     back down is how a failed add leaves a phantom pair sitting in the cart. */
  window.sbsOptimisticLine = renderOptimisticLine;
  window.sbsOptimisticRollback = rollbackOptimistic;
})();
